import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import { nsSharedStyles } from '../ns-styles/index.js';
import { renderDate } from '../helpers/format-render.js';
import { formatCredits, readingCredits } from '../helpers/public-ledger-credits.js';
import { isLedgerNetwork, ledgerExplorerUrl } from '../helpers/public-ledger-network.js';
import type { LedgerBalanceReading } from '../models/company.js';

/**
 * One public ledger account's balance: a label, the balance in nodeStream credits, the account id, a link to the account on
 * the public ledger explorer and when it was read. It draws no surface of its own, so it sits on a light job panel or a
 * black account box alike; text takes the surrounding colour. Set `.reading` to a stored reading
 * (`company.ledgerCred.balances.*`); `copyable` adds a Copy button for the account id.
 */
@customElement('ns-ledger-balance')
export class NSLedgerBalance extends LitElement {
  @property({ type: String })
  label = '';

  @property({ attribute: false })
  reading?: LedgerBalanceReading | null;

  // Shown in place of the figure when there is no reading yet
  @property({ type: String, attribute: 'empty-text' })
  emptyText = 'Not read yet';

  @property({ type: Boolean })
  copyable = false;

  @state()
  private __copied = false;

  static styles = [
    nsSharedStyles,
    css`
      :host {
        display: block;
      }

      :host([hidden]) {
        display: none;
      }

      .label {
        margin: 0;
        font-size: var(--wa-font-size-xs);
        font-weight: 500;
        text-transform: uppercase;
      }

      .figure {
        margin: var(--wa-space-2xs) 0 0;
        font-size: var(--wa-font-size-xl);
        font-weight: 700;
        line-height: 1.2;
      }

      .figure .unit {
        font-size: var(--wa-font-size-m);
        font-weight: 300;
      }

      .figure.empty {
        font-size: var(--wa-font-size-m);
        font-weight: 300;
      }

      .account {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--wa-space-xs);
        margin: var(--wa-space-xs) 0 0;
        font-size: var(--wa-font-size-s);
      }

      .account code {
        font-family: inherit;
        font-weight: 500;
      }

      .account a {
        color: var(--wa-color-text-link);
        font-weight: 500;
      }

      .as-of {
        margin: var(--wa-space-2xs) 0 0;
        font-size: var(--wa-font-size-xs);
      }
    `,
  ];

  private async __copy(accountId: string) {
    try {
      await navigator.clipboard.writeText(accountId);
      this.__copied = true;
    } catch {
      this.__copied = false;
    }
  }

  render() {
    const reading = this.reading;
    const credits = readingCredits(reading);
    const accountId = reading?.accountId || null;
    const network = isLedgerNetwork(reading?.network) ? reading.network : null;
    return html`
      ${this.label
        ? html`
            <p class="label">${this.label}</p>
          `
        : nothing}
      ${credits === null
        ? html`
            <p class="figure empty">${this.emptyText}</p>
          `
        : html`
            <p class="figure">
              ${formatCredits(credits)}
              <span class="unit">credits</span>
            </p>
          `}
      ${accountId
        ? html`
            <p class="account">
              <span>
                Account
                <code>${accountId}</code>
              </span>
              ${this.copyable
                ? html`
                    <wa-button size="s" appearance="outlined" variant="brand" @click=${() => this.__copy(accountId)}>
                      Copy
                      <span class="visually-hidden">the account id</span>
                    </wa-button>
                    <span role="status">${this.__copied ? 'Copied' : ''}</span>
                  `
                : nothing}
              ${network
                ? html`
                    <a
                      href=${ledgerExplorerUrl(network, 'account', accountId)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View on the public ledger
                      <span class="visually-hidden">(opens in a new tab)</span>
                    </a>
                  `
                : nothing}
            </p>
          `
        : nothing}
      ${reading?.readAt
        ? html`
            <p class="as-of">As of ${renderDate(reading.readAt, 'dateTime')}</p>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ns-ledger-balance': NSLedgerBalance;
  }
}
